import GIF from "gif.js";
import { useState, useRef, useEffect } from "react";
import {BrowserRouter as Router, useParams, NavLink } from "react-router-dom";
import { spectronShader } from './extra/shader.js';
import {motion,AnimatePresence } from "framer-motion";


const gifVariant = {
  hidden: {
      y:20,
      opacity: 0
  },
  visible: {
      y:0,
      opacity:1,
      transition: {
          delay: 0.1, 
          duration: 0.5
      }
  },
  done: {
      y:-20,
      opacity: 0
  },
}

const getFromHash = h => {
  h = h.substr(2);
  let table = [];
  for (let i = 0; i < 16; i++) { table[i] = parseInt("0x"+(h.substr(i+8, 1) + h.substr(i+24, 1) + h.substr(i+40, 1)),16)/4096; }
  return table;
}

const initWebGL = canvas => {
  const gl = canvas.getContext('webgl');
  const shader = {frag_code: spectronShader.fragment, vert_code: spectronShader.vertex};
  let uniforms, attributes;
  /* Fragment shader */
  shader.frag = gl.createShader(gl.FRAGMENT_SHADER);       
    gl.shaderSource(shader.frag, shader.frag_code);       
    gl.compileShader(shader.frag);
    if (!gl.getShaderParameter(shader.frag, gl.COMPILE_STATUS)) { throw "Fragment shader:" + gl.getShaderInfoLog(shader.frag);}
  /* Vertex shader */
  shader.vert = gl.createShader(gl.VERTEX_SHADER);       
    gl.shaderSource(shader.vert, shader.vert_code);       
    gl.compileShader(shader.vert);
    if (!gl.getShaderParameter(shader.vert, gl.COMPILE_STATUS)) { throw "Vertex shader:" + gl.getShaderInfoLog(shader.vert); }
  /* Link program */
  shader.prog = gl.createProgram();
    gl.attachShader(shader.prog, shader.vert);
    gl.attachShader(shader.prog, shader.frag);
    gl.linkProgram(shader.prog);
    var success = gl.getProgramParameter(shader.prog, gl.LINK_STATUS);
    if (!success) { throw ("Failed to link shaders:" + gl.getProgramInfoLog(shader.prog));}
    gl.detachShader(shader.prog, shader.vert);
    gl.detachShader(shader.prog, shader.frag);
    gl.deleteShader(shader.frag);
    gl.deleteShader(shader.vert);
    gl.useProgram(shader.prog);
  /* Define uniforms */
  uniforms = {
    /*
    "u_resolution": gl.getUniformLocation(shader.prog, "u_resolution"),
    "u_time": gl.getUniformLocation(shader.prog, "u_time"), 
    "u_generating": gl.getUniformLocation(shader.prog, "u_generating"),
    "u_hash": gl.getUniformLocation(shader.prog, "u_hash")
    */
    "u_resolution": gl.getUniformLocation(shader.prog, "D"),
    "u_time": gl.getUniformLocation(shader.prog, "m"), 
    "u_generating": gl.getUniformLocation(shader.prog, "I"),
    "u_hash": gl.getUniformLocation(shader.prog, "c")
  }
  /* Define attributes */
  attributes = {
    "a_position": gl.getAttribLocation(shader.prog, "position")
  }
  /* Define buffer */
  shader.buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, shader.buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,-1,1,1,1,1,1,1,-1,-1,-1]), gl.STATIC_DRAW);
  return [gl, uniforms, attributes, shader];
}

const GifExport = props => {
  // Parameters
  let { id } = useParams();
  let frameId, gl, uniforms, attributes, shader;
  // References
  const canvasRef = useRef();
  const counterRef = useRef(0);
  const hashTableRef = useRef();
  const gifFrameRef = useRef(0);
  // States
  const [gifStatus, setGifStatus] = useState("Loading hash...");
  const [gifRecorder, setGifRecorder] = useState(null);
  const [gifSrc, setGifSrc] = useState("");
  const [currentScreen, setCurrentScreen] = useState(0);

  const gifData = { step: 6, frames: 34, delay: 100, size: {width: 300, height: 225} };


  const isValidToken = id => {
    let allTokensId = [];
      for (let i = 0; i < props.blockchain.artblock.projectTokenInfo.invocations; i++) {
        allTokensId[i] = props.blockchain.artblock.tokenPrefix + i;    
      }
    let isValid = allTokensId.filter((e)=> e.toString() === id.toString());
    if (isValid.length > 0) {
      return true;
    } else {
      return false;
    }
  }


  const renderScreens = screenID => {
    switch (screenID) {
      case -1:
      return (
      <>
        <motion.div key="step-gif-3" className="container has-text-black has-text-centered sp-buy" initial="hidden" animate="visible" exit="done" variants={gifVariant}>
        <p className="is-size-1 is-size-3-mobile">Ouch!</p>
        <p className="is-size-7 mt-4">Can't generate animated GIF.<br/>{id} is not a valid Spectron token.</p>
       </motion.div>
       <motion.footer className="section has-text-black">
         {props.fastload ? 
            <a href="/" className="button is-small is-primary is-rounded is-outlined">&larr; Home</a>
         :
            <NavLink to="/gallery" className="button is-small is-primary is-rounded is-outlined">&larr; Gallery</NavLink>
         }
       </motion.footer>
      </>
      )
      case 0:
      return (
        <motion.div key="step-gif-2" className="container has-text-black has-text-centered sp-buy" initial="hidden" animate="visible" exit="done" variants={gifVariant}>
        <p className="is-size-7 mt-4">{gifStatus}<br/>Generating animated GIF for Spectron {id-props.blockchain.artblock.tokenPrefix}.</p>
        <p className="sp-loader mt-4"></p>      
        <canvas className="is-invisible" ref={canvasRef} />
       </motion.div>
      )
      case 1: 
      return (
        <>
          <motion.div key="step-gif-1" className="container has-text-black has-text-centered sp-buy" initial="hidden" animate="visible" exit="done" variants={gifVariant}>
          <p className="is-size-1 is-size-3-mobile"><i className="fa fa-check-circle"></i></p>
          <p className="is-size-7 mt-4">Generated animated GIF for Spectron {id-props.blockchain.artblock.tokenPrefix}.<br/>Download from <a className="has-text-black sp-em" target="_blank" href={gifSrc}>this link</a> or just save the image below.</p>
          <img className="mt-6" src={gifSrc} width={gifData.size.width} height={gifData.size.height} />
          </motion.div>
          <motion.footer className="section has-text-black">
         {props.fastload ? 
            <a href="/" className="button is-small is-primary is-rounded is-outlined">&larr; Home</a>
         :
            <NavLink to="/gallery" className="button is-small is-primary is-rounded is-outlined">&larr; Gallery</NavLink>
         }
       </motion.footer>
        </>
      )
    }
  }
  const finishRecording = data => {
    console.log("Finished recording!");
    setGifStatus("Recording complete!");
    gifFrameRef.current = 0;
    setGifSrc(URL.createObjectURL(data));
    console.log("Cleaning up webGl context.");
    gl.deleteProgram(shader.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.deleteBuffer(shader.buffer); 
  }

  const render = () => {
    counterRef.current = counterRef.current + 0.16;
    gl.uniform2f(uniforms.u_resolution, gl.canvas.width, gl.canvas.height);
    gl.uniform1f(uniforms.u_time, counterRef.current);
    gl.uniform1f(uniforms.u_generating, 0);
    gl.uniform1fv(uniforms.u_hash, hashTableRef.current);
    gl.enableVertexAttribArray(attributes.a_position);
    gl.vertexAttribPointer(attributes.a_position, 2, gl.FLOAT, false, 0, 0);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.clearColor(0, 0, 0, 0);      
    gl.drawArrays(gl.TRIANGLES, 0, 6);
      if (gifFrameRef.current < (gifData.step*gifData.frames)) {
        if (gifFrameRef.current%gifData.step == 0) {
          setGifStatus("Recording " + gifFrameRef.current/gifData.step + " of " + gifData.frames + " frames.");
          gifRecorder.addFrame(gl.canvas, {delay: gifData.delay, copy: true});
        }
        gifFrameRef.current = gifFrameRef.current + 1;
        frameId = requestAnimationFrame(render);
      } else {
        gifRecorder.render();
        cancelAnimationFrame(frameId); 
      }
  }

  useEffect(()=> {
    if (gifSrc) {
      setCurrentScreen(1); 
    }

  },[gifSrc])

  useEffect(() => {
    const loadToken = async() => {
      /*const token = props.tokenList.filter(tokens => (tokens.id === id - 12000000));
      if (token.length > 0) {
        console.log("Ok, value already present");
        hashTableRef.current = getFromHash(token[0].hash);
      } else {
        console.log("Value not present, gonna web3 it");
        let hash = await props.blockchain.infura.contract.methods.tokenIdToHash( id).call();
        hashTableRef.current = getFromHash(hash);
        console.log(hashTableRef.current);
      }*/
        let hash = await props.blockchain.infura.contract.methods.tokenIdToHash( id).call();
        hashTableRef.current = getFromHash(hash);
        

      if (!gifRecorder) {
        setGifRecorder(new GIF({width:gifData.size.width, height:gifData.size.height, workers: 2, dither: "Atkinson", workerScript: process.env.PUBLIC_URL + '/gif.worker.js', quality: 80 }));
      }    
    }
    if (isValidToken(id)) {
      setCurrentScreen(0); 
      loadToken();
    } else {
      setCurrentScreen(-1);
      console.log("Token is invalid!");
    }
    
  },[]);


  useEffect(() => {
    if (gifRecorder && canvasRef.current) {
      gifRecorder.on('finished', finishRecording);
      canvasRef.current.width = gifData.size.width;
      canvasRef.current.height = gifData.size.height;
      [gl, uniforms, attributes, shader] = initWebGL(canvasRef.current);
      render();
      //console.log("gif.js has been initialized correcly!");
    }
  },[gifRecorder]);

    return (
      
     <AnimatePresence exitBeforeEnter>
         {renderScreens(currentScreen)}
      </AnimatePresence>
    )
}

export default GifExport;














